import React, { useRef, useEffect, useState } from 'react';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Autoplay, Navigation, Pagination, EffectCoverflow } from 'swiper/modules';
import { 
  ChevronLeft, 
  ChevronRight, 
  Code, 
  Cloud, 
  Shield, 
  Video, 
  Headphones,
  Sparkles,
  Cpu,
  Globe,
  Zap,
  Layers,
  Box,
  Terminal,
  GitBranch,
  Workflow
} from 'lucide-react';

// Import Swiper styles
import 'swiper/css';
import 'swiper/css/autoplay';
import 'swiper/css/navigation';
import 'swiper/css/pagination';
import 'swiper/css/effect-coverflow';

// Devicon CSS for technology icons
import 'devicon/devicon.min.css';

const EngineeringStack = () => {
  const [activeCategory, setActiveCategory] = useState('fullstack');
  const [isHovering, setIsHovering] = useState(false);
  
  // Create refs for each category's navigation buttons
  const navRefs = {
    fullstack: { prev: useRef(null), next: useRef(null) },
    cloudDevops: { prev: useRef(null), next: useRef(null) },
    security: { prev: useRef(null), next: useRef(null) },
    cctv: { prev: useRef(null), next: useRef(null) },
    support: { prev: useRef(null), next: useRef(null) },
  };

  const swiperRefs = {
    fullstack: useRef(null),
    cloudDevops: useRef(null),
    security: useRef(null),
    cctv: useRef(null),
    support: useRef(null),
  };

  // Category navigation tabs
  const categoryTabs = [
    { id: 'fullstack', label: 'Full Stack', icon: Layers, gradient: 'from-logo-darkblue to-logo-lightblue' },
    { id: 'cloudDevops', label: 'Cloud & DevOps', icon: Cloud, gradient: 'from-logo-lightblue to-logo-teal' },
    { id: 'security', label: 'Security', icon: Shield, gradient: 'from-logo-darkblue to-logo-teal' },
    { id: 'cctv', label: 'Physical Security', icon: Video, gradient: 'from-logo-yellow to-logo-green' },
    { id: 'support', label: 'IT Support', icon: Headphones, gradient: 'from-logo-teal to-logo-green3' },
  ];

  // Updated categories with merged technologies
  const categories = [
    {
      id: 'fullstack',
      title: 'Full Stack Development',
      description: 'Frontend, Backend, Mobile & Database Technologies',
      icon: Layers,
      gradient: 'bg-gradient-to-r from-logo-darkblue to-logo-lightblue',
      lightBg: 'bg-blue-50',
      technologies: [
        // Frontend
        { name: 'React', icon: 'devicon-react-original', colored: true, category: 'Frontend' },
        { name: 'Angular', icon: 'devicon-angularjs-plain', colored: true, category: 'Frontend' },
        { name: 'Vue.js', icon: 'devicon-vuejs-plain', colored: true, category: 'Frontend' },
        { name: 'Next.js', icon: 'devicon-nextjs-original', colored: false, category: 'Frontend' },
        { name: 'TypeScript', icon: 'devicon-typescript-plain', colored: true, category: 'Frontend' },
        { name: 'JavaScript', icon: 'devicon-javascript-plain', colored: true, category: 'Frontend' },
        { name: 'HTML5', icon: 'devicon-html5-plain', colored: true, category: 'Frontend' },
        { name: 'CSS3', icon: 'devicon-css3-plain', colored: true, category: 'Frontend' },
        { name: 'Tailwind CSS', icon: 'devicon-tailwindcss-plain', colored: true, category: 'Frontend' },
        { name: 'Bootstrap', icon: 'devicon-bootstrap-plain', colored: true, category: 'Frontend' },
        // Backend
        { name: 'Node.js', icon: 'devicon-nodejs-plain', colored: true, category: 'Backend' },
        { name: 'Python', icon: 'devicon-python-plain', colored: true, category: 'Backend' },
        { name: 'Java', icon: 'devicon-java-plain', colored: true, category: 'Backend' },
        { name: 'PHP', icon: 'devicon-php-plain', colored: true, category: 'Backend' },
        { name: 'Go', icon: 'devicon-go-plain', colored: true, category: 'Backend' },
        { name: 'Ruby', icon: 'devicon-ruby-plain', colored: true, category: 'Backend' },
        { name: 'C#', icon: 'devicon-csharp-plain', colored: true, category: 'Backend' },
        { name: 'Rust', icon: 'devicon-rust-plain', colored: false, category: 'Backend' },
        { name: 'Kotlin', icon: 'devicon-kotlin-plain', colored: true, category: 'Backend' },
        { name: 'Swift', icon: 'devicon-swift-plain', colored: true, category: 'Backend' },
        { name: 'GraphQL', icon: 'devicon-graphql-plain', colored: true, category: 'Backend' },
        { name: 'REST API', icon: 'devicon-postman-plain', colored: true, category: 'Backend' },
        // Mobile
        { name: 'React Native', icon: 'devicon-react-original', colored: true, category: 'Mobile' },
        { name: 'Flutter', icon: 'devicon-flutter-plain', colored: true, category: 'Mobile' },
        { name: 'Swift (iOS)', icon: 'devicon-swift-plain', colored: true, category: 'Mobile' },
        { name: 'Kotlin (Android)', icon: 'devicon-kotlin-plain', colored: true, category: 'Mobile' },
        { name: 'Android', icon: 'devicon-android-plain', colored: true, category: 'Mobile' },
        { name: 'iOS', icon: 'devicon-apple-original', colored: false, category: 'Mobile' },
        { name: 'Xamarin', icon: 'devicon-xamarin-plain', colored: true, category: 'Mobile' },
        { name: 'Ionic', icon: 'devicon-ionic-original', colored: true, category: 'Mobile' },
        // Databases
        { name: 'PostgreSQL', icon: 'devicon-postgresql-plain', colored: true, category: 'Database' },
        { name: 'MySQL', icon: 'devicon-mysql-plain', colored: true, category: 'Database' },
        { name: 'MongoDB', icon: 'devicon-mongodb-plain', colored: true, category: 'Database' },
        { name: 'Redis', icon: 'devicon-redis-plain', colored: true, category: 'Database' },
        { name: 'Elasticsearch', icon: 'devicon-elasticsearch-plain', colored: true, category: 'Database' },
        { name: 'Cassandra', icon: 'devicon-cassandra-plain', colored: true, category: 'Database' },
        { name: 'MariaDB', icon: 'devicon-mariadb-plain', colored: true, category: 'Database' },
        { name: 'SQLite', icon: 'devicon-sqlite-plain', colored: true, category: 'Database' },
        { name: 'Firebase', icon: 'devicon-firebase-plain', colored: true, category: 'Database' },
        { name: 'DynamoDB', icon: 'devicon-dynamodb-plain', colored: true, category: 'Database' },
      ],
    },
    {
      id: 'cloudDevops',
      title: 'Cloud Infrastructure & DevOps',
      description: 'Cloud Platforms, Infrastructure as Code, CI/CD & Monitoring',
      icon: Cloud,
      gradient: 'bg-gradient-to-r from-logo-lightblue to-logo-teal',
      lightBg: 'bg-cyan-50',
      technologies: [
        // Cloud Platforms
        { name: 'AWS', icon: 'devicon-amazonwebservices-plain-wordmark', colored: true, category: 'Cloud' },
        { name: 'Azure', icon: 'devicon-azure-plain', colored: true, category: 'Cloud' },
        { name: 'Google Cloud', icon: 'devicon-googlecloud-plain', colored: true, category: 'Cloud' },
        // Containerization & Orchestration
        { name: 'Docker', icon: 'devicon-docker-plain', colored: true, category: 'Container' },
        { name: 'Kubernetes', icon: 'devicon-kubernetes-plain', colored: true, category: 'Container' },
        // Infrastructure as Code
        { name: 'Terraform', icon: 'devicon-terraform-plain', colored: true, category: 'IaC' },
        { name: 'Ansible', icon: 'devicon-ansible-plain', colored: true, category: 'IaC' },
        { name: 'Vagrant', icon: 'devicon-vagrant-plain', colored: true, category: 'IaC' },
        // CI/CD Tools
        { name: 'Jenkins', icon: 'devicon-jenkins-plain', colored: true, category: 'CI/CD' },
        { name: 'GitLab CI', icon: 'devicon-gitlab-plain', colored: true, category: 'CI/CD' },
        { name: 'GitHub Actions', icon: 'devicon-githubactions-plain', colored: true, category: 'CI/CD' },
        { name: 'CircleCI', icon: 'devicon-circleci-plain', colored: true, category: 'CI/CD' },
        { name: 'Travis CI', icon: 'devicon-travis-plain', colored: true, category: 'CI/CD' },
        { name: 'ArgoCD', icon: 'devicon-argocd-plain', colored: true, category: 'CI/CD' },
        { name: 'Helm', icon: 'devicon-helm-plain', colored: true, category: 'CI/CD' },
        // Monitoring & Observability
        { name: 'Prometheus', icon: 'devicon-prometheus-original', colored: true, category: 'Monitoring' },
        { name: 'Grafana', icon: 'devicon-grafana-original', colored: true, category: 'Monitoring' },
        { name: 'Datadog', icon: 'devicon-datadog-plain', colored: true, category: 'Monitoring' },
        { name: 'New Relic', icon: 'devicon-newrelic-plain', colored: true, category: 'Monitoring' },
        { name: 'Elastic Stack', icon: 'devicon-elasticstack-plain', colored: true, category: 'Monitoring' },
        { name: 'Jaeger', icon: 'devicon-jaeger-plain', colored: true, category: 'Monitoring' },
      ],
    },
    {
      id: 'security',
      title: 'Cybersecurity',
      description: 'Security Tools, Compliance & Identity Management',
      icon: Shield,
      gradient: 'bg-gradient-to-r from-logo-darkblue to-logo-teal',
      lightBg: 'bg-indigo-50',
      technologies: [
        { name: 'OWASP', icon: 'devicon-owasp-plain', colored: false, category: 'Security' },
        { name: 'SonarQube', icon: 'devicon-sonarqube-plain', colored: true, category: 'Security' },
        { name: 'Snyk', icon: 'devicon-snyk-plain', colored: true, category: 'Security' },
        { name: 'Auth0', icon: 'devicon-auth0-plain', colored: true, category: 'Identity' },
        { name: 'Keycloak', icon: 'devicon-keycloak-plain', colored: true, category: 'Identity' },
        { name: 'Okta', icon: 'devicon-okta-plain', colored: true, category: 'Identity' },
        { name: 'Wireshark', icon: 'devicon-wireshark-plain', colored: true, category: 'Network' },
        { name: 'Python (Security)', icon: 'devicon-python-plain', colored: true, category: 'Security' },
        { name: 'Bash Scripting', icon: 'devicon-bash-plain', colored: true, category: 'Scripting' },
        { name: 'Kali Linux', icon: 'devicon-linux-plain', colored: true, category: 'Security' },
      ],
    },
    {
      id: 'cctv',
      title: 'Physical Security & CCTV',
      description: 'Video Analytics, Computer Vision & IoT',
      icon: Video,
      gradient: 'bg-gradient-to-r from-logo-yellow to-logo-green',
      lightBg: 'bg-green-50',
      technologies: [
        { name: 'OpenCV', icon: 'devicon-opencv-plain', colored: true, category: 'Computer Vision' },
        { name: 'Python', icon: 'devicon-python-plain', colored: true, category: 'Programming' },
        { name: 'TensorFlow', icon: 'devicon-tensorflow-original', colored: true, category: 'AI/ML' },
        { name: 'CUDA', icon: 'devicon-cuda-plain', colored: true, category: 'GPU' },
        { name: 'Raspberry Pi', icon: 'devicon-raspberrypi-plain', colored: true, category: 'IoT' },
        { name: 'Arduino', icon: 'devicon-arduino-plain', colored: true, category: 'IoT' },
        { name: 'C++', icon: 'devicon-cplusplus-plain', colored: true, category: 'Programming' },
        { name: 'FFmpeg', icon: 'devicon-ffmpeg-plain', colored: true, category: 'Video' },
        { name: 'YOLO', icon: 'devicon-python-plain', colored: true, category: 'AI/ML' },
        { name: 'RTSP', icon: 'devicon-ffmpeg-plain', colored: true, category: 'Protocol' },
      ],
    },
    {
      id: 'support',
      title: 'IT Support & Tools',
      description: 'Operating Systems, Version Control & Collaboration',
      icon: Headphones,
      gradient: 'bg-gradient-to-r from-logo-teal to-logo-green3',
      lightBg: 'bg-emerald-50',
      technologies: [
        // Operating Systems
        { name: 'Linux', icon: 'devicon-linux-plain', colored: true, category: 'OS' },
        { name: 'Ubuntu', icon: 'devicon-ubuntu-plain', colored: true, category: 'OS' },
        { name: 'Windows', icon: 'devicon-windows11-original', colored: true, category: 'OS' },
        { name: 'MacOS', icon: 'devicon-apple-original', colored: false, category: 'OS' },
        // Scripting & Shell
        { name: 'Bash', icon: 'devicon-bash-plain', colored: true, category: 'Scripting' },
        { name: 'PowerShell', icon: 'devicon-powershell-plain', colored: true, category: 'Scripting' },
        // Development Tools
        { name: 'VSCode', icon: 'devicon-vscode-plain', colored: true, category: 'IDE' },
        { name: 'Git', icon: 'devicon-git-plain', colored: true, category: 'Version Control' },
        { name: 'GitHub', icon: 'devicon-github-original', colored: false, category: 'Version Control' },
        { name: 'GitLab', icon: 'devicon-gitlab-plain', colored: true, category: 'Version Control' },
        // Collaboration
        { name: 'Jira', icon: 'devicon-jira-plain', colored: true, category: 'Project Management' },
        { name: 'Confluence', icon: 'devicon-confluence-plain', colored: true, category: 'Documentation' },
        { name: 'Slack', icon: 'devicon-slack-plain', colored: true, category: 'Communication' },
        { name: 'Teams', icon: 'devicon-microsoftteams-plain', colored: true, category: 'Communication' },
      ],
    },
  ];

  useEffect(() => {
    // Initialize navigation for each swiper
    categories.forEach((category) => {
      if (swiperRefs[category.id]?.current && navRefs[category.id]) {
        const swiper = swiperRefs[category.id].current;
        swiper.params.navigation.prevEl = navRefs[category.id].prev.current;
        swiper.params.navigation.nextEl = navRefs[category.id].next.current;
        swiper.navigation.init();
        swiper.navigation.update();
      }
    });
  }, [categories]);

  // Get current category data
  const currentCategory = categories.find(c => c.id === activeCategory) || categories[0];

  return (
    <section id="engineering-stack" className="py-16 sm:py-20 bg-gradient-to-b from-gray-50 to-white relative overflow-hidden">
      {/* Background Tech Pattern */}
      <div className="absolute inset-0 opacity-5 pointer-events-none">
        <div className="absolute top-0 left-0 w-full h-full">
          <svg width="100%" height="100%" xmlns="http://www.w3.org/2000/svg">
            <defs>
              <pattern id="tech-pattern" x="0" y="0" width="40" height="40" patternUnits="userSpaceOnUse">
                <circle cx="20" cy="20" r="1" fill="#07589a" />
                <circle cx="60" cy="20" r="1" fill="#40b3eb" />
                <circle cx="100" cy="20" r="1" fill="#24a37d" />
                <path d="M20 20 L40 20" stroke="#07589a" strokeWidth="0.5" strokeDasharray="2 2" />
                <path d="M60 20 L80 20" stroke="#40b3eb" strokeWidth="0.5" strokeDasharray="2 2" />
              </pattern>
            </defs>
            <rect width="100%" height="100%" fill="url(#tech-pattern)" />
          </svg>
        </div>
      </div>

      <div className="container mx-auto px-4 sm:px-6 relative z-10">
        {/* Header with animated elements */}
        <div className="text-center mb-8 sm:mb-12">
          <div className="inline-flex items-center justify-center p-2 bg-gradient-to-r from-logo-darkblue/10 to-logo-lightblue/10 rounded-full mb-4">
            <div className="flex items-center space-x-2 bg-white px-4 py-2 rounded-full shadow-sm">
              <Cpu className="text-logo-darkblue" size={20} />
              <span className="text-sm font-medium text-logo-navy">Technology Stack</span>
              <Zap className="text-logo-yellow" size={16} />
            </div>
          </div>
          
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-logo-navy mb-4">
            Our{' '}
            <span className="bg-gradient-to-r from-logo-darkblue to-logo-lightblue bg-clip-text text-transparent">
              Engineering Stack
            </span>
          </h2>
          
          <p className="text-gray-600 max-w-3xl mx-auto text-base sm:text-lg mb-8">
            We leverage cutting-edge technologies across our service offerings to deliver 
            enterprise-grade solutions tailored to your needs.
          </p>

          {/* Tech Stats */}
          <div className="flex flex-wrap justify-center gap-4 sm:gap-8 mb-8">
            <div className="flex items-center space-x-2 bg-white px-4 py-2 rounded-lg shadow-sm">
              <Box className="text-logo-darkblue" size={20} />
              <span className="text-sm font-medium text-gray-700">
                <span className="text-logo-darkblue font-bold">{categories.reduce((acc, cat) => acc + cat.technologies.length, 0)}+</span> Technologies
              </span>
            </div>
            <div className="flex items-center space-x-2 bg-white px-4 py-2 rounded-lg shadow-sm">
              <GitBranch className="text-logo-green2" size={20} />
              <span className="text-sm font-medium text-gray-700">
                <span className="text-logo-green2 font-bold">{categories.length}</span> Categories
              </span>
            </div>
            <div className="flex items-center space-x-2 bg-white px-4 py-2 rounded-lg shadow-sm">
              <Workflow className="text-logo-teal" size={20} />
              <span className="text-sm font-medium text-gray-700">
                <span className="text-logo-teal font-bold">24/7</span> Support
              </span>
            </div>
          </div>
        </div>

        {/* Category Tabs - Modern Pill Navigation */}
        <div className="flex flex-wrap justify-center gap-3 mb-12">
          {categoryTabs.map((tab) => {
            const Icon = tab.icon;
            const isActive = activeCategory === tab.id;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveCategory(tab.id)}
                className={`relative group px-5 py-3 rounded-full transition-all duration-300 overflow-hidden
                          ${isActive 
                            ? `bg-gradient-to-r ${tab.gradient} text-white shadow-lg scale-105` 
                            : 'bg-white text-gray-600 hover:text-logo-navy hover:shadow-md'
                          }`}
              >
                {/* Hover Effect Background */}
                <div className={`absolute inset-0 bg-gradient-to-r ${tab.gradient} opacity-0 group-hover:opacity-10 transition-opacity duration-300`}></div>
                
                <div className="relative flex items-center space-x-2">
                  <Icon size={18} />
                  <span className="text-sm font-medium whitespace-nowrap">{tab.label}</span>
                </div>
                
                {/* Active Indicator Dot */}
                {isActive && (
                  <span className="absolute -bottom-1 left-1/2 transform -translate-x-1/2 w-1 h-1 bg-white rounded-full"></span>
                )}
              </button>
            );
          })}
        </div>

        {/* Active Category Display */}
        <div className="mb-8 text-center">
          <h3 className="text-2xl font-bold text-logo-navy mb-2">
            {currentCategory.title}
          </h3>
          <p className="text-gray-500">
            {currentCategory.description}
          </p>
        </div>

        {/* Main Carousel with Enhanced Design */}
        <div className="relative group/carousel">
          {/* Gradient Background Effect */}
          <div className={`absolute -inset-4 bg-gradient-to-r ${currentCategory.gradient} opacity-5 blur-2xl rounded-3xl transition-opacity duration-500`}></div>
          
          {/* Category Header with Navigation */}
          <div className="relative">
            {/* Swiper Carousel */}
            <Swiper
              onSwiper={(swiper) => {
                swiperRefs[currentCategory.id].current = swiper;
              }}
              modules={[Autoplay, Navigation, Pagination, EffectCoverflow]}
              spaceBetween={20}
              slidesPerView={2}
              autoplay={{
                delay: 3000,
                disableOnInteraction: false,
                pauseOnMouseEnter: true,
              }}
              navigation={{
                prevEl: navRefs[currentCategory.id]?.prev.current,
                nextEl: navRefs[currentCategory.id]?.next.current,
              }}
              pagination={{ 
                clickable: true,
                el: `.swiper-pagination-${currentCategory.id}`,
                bulletClass: 'swiper-pagination-bullet custom-bullet',
                bulletActiveClass: 'swiper-pagination-bullet-active custom-bullet-active',
              }}
              breakpoints={{
                480: { slidesPerView: 3, spaceBetween: 20 },
                640: { slidesPerView: 4, spaceBetween: 20 },
                768: { slidesPerView: 5, spaceBetween: 20 },
                1024: { slidesPerView: 6, spaceBetween: 20 },
                1280: { slidesPerView: 7, spaceBetween: 20 },
              }}
              className="engineering-stack-carousel py-4"
            >
              {currentCategory.technologies.map((tech, index) => (
                <SwiperSlide key={`${currentCategory.id}-${index}`}>
                  <div 
                    className="tech-card group relative bg-white rounded-xl p-4 sm:p-6
                              border border-gray-100 shadow-sm hover:shadow-xl
                              transition-all duration-500 hover:-translate-y-2
                              hover:border-transparent cursor-default
                              flex flex-col items-center text-center
                              before:absolute before:inset-0 before:rounded-xl
                              before:bg-gradient-to-r before:from-transparent before:via-white/50 before:to-transparent
                              before:translate-x-[-100%] hover:before:translate-x-[100%]
                              before:transition-transform before:duration-1000
                              before:pointer-events-none overflow-hidden"
                    style={{
                      background: `linear-gradient(135deg, white 0%, ${currentCategory.lightBg} 100%)`,
                    }}
                  >
                    {/* Glow Effect on Hover */}
                    <div className={`absolute inset-0 opacity-0 group-hover:opacity-20 transition-opacity duration-500 rounded-xl
                                  bg-gradient-to-r ${currentCategory.gradient}`}></div>
                    
                    {/* Icon Container */}
                    <div className="relative mb-3 transform group-hover:scale-110 transition-transform duration-300">
                      <i className={`${tech.icon} ${tech.colored ? 'colored' : ''} text-3xl sm:text-4xl`}></i>
                    </div>
                    
                    {/* Technology Name */}
                    <span className="relative text-xs sm:text-sm font-medium text-gray-700 group-hover:text-logo-darkblue transition-colors duration-300">
                      {tech.name}
                    </span>
                    
                    {/* Category Badge (optional) */}
                    {tech.category && (
                      <span className={`absolute top-2 right-2 text-[8px] px-1.5 py-0.5 rounded-full
                                     bg-white/80 text-gray-500 opacity-0 group-hover:opacity-100 transition-opacity duration-300`}>
                        {tech.category}
                      </span>
                    )}
                  </div>
                </SwiperSlide>
              ))}
            </Swiper>

            {/* Custom Navigation Buttons - Positioned Absolutely */}
            <button
              ref={navRefs[currentCategory.id]?.prev}
              className={`absolute left-0 top-1/2 -translate-y-1/2 -translate-x-4 lg:-translate-x-6
                         w-10 h-10 rounded-full bg-white shadow-lg border border-gray-200 
                         flex items-center justify-center z-20
                         hover:${currentCategory.gradient} hover:text-white hover:border-transparent
                         transition-all duration-300 opacity-0 group-hover/carousel:opacity-100
                         focus:outline-none`}
              aria-label={`Previous ${currentCategory.title}`}
            >
              <ChevronLeft size={20} />
            </button>
            
            <button
              ref={navRefs[currentCategory.id]?.next}
              className={`absolute right-0 top-1/2 -translate-y-1/2 translate-x-4 lg:translate-x-6
                         w-10 h-10 rounded-full bg-white shadow-lg border border-gray-200 
                         flex items-center justify-center z-20
                         hover:${currentCategory.gradient} hover:text-white hover:border-transparent
                         transition-all duration-300 opacity-0 group-hover/carousel:opacity-100
                         focus:outline-none`}
              aria-label={`Next ${currentCategory.title}`}
            >
              <ChevronRight size={20} />
            </button>
          </div>

          {/* Custom Pagination */}
          <div className={`swiper-pagination-${currentCategory.id} flex justify-center mt-8`}></div>
        </div>

        {/* Bottom Stats */}
        <div className="mt-12 grid grid-cols-2 sm:grid-cols-4 gap-4">
          <div className="text-center">
            <div className="text-2xl font-bold text-logo-darkblue">
              {currentCategory.technologies.length}+
            </div>
            <div className="text-xs text-gray-500">Technologies</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-logo-green2">
              {new Set(currentCategory.technologies.map(t => t.category)).size}
            </div>
            <div className="text-xs text-gray-500">Categories</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-logo-teal">
              {Math.floor(currentCategory.technologies.length / 5)}+
            </div>
            <div className="text-xs text-gray-500">Years Experience</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-logo-yellow">
              {currentCategory.technologies.filter(t => t.colored).length}
            </div>
            <div className="text-xs text-gray-500">Active Projects</div>
          </div>
        </div>

        {/* Note about technologies */}
        <div className="mt-12 text-center">
          <div className="inline-flex items-center space-x-2 bg-gray-100 px-4 py-2 rounded-full">
            <Sparkles className="text-logo-yellow" size={16} />
            <p className="text-xs sm:text-sm text-gray-600">
              We continuously update our stack with emerging technologies to ensure cutting-edge solutions.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};

export default EngineeringStack;